import { lazy, Suspense } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import MainLayout from '../layouts/main';
// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
import { PATH_DASHBOARD } from './paths';
import { menuConfig, menuPrerelease } from '../layouts/main/MenuConfig';
// components
import LoadingScreen from '../components/LoadingScreen';
// hooks
import useUtmCapture from '../hooks/useUtmCapture';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  useUtmCapture();
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected/:refered_by', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password/:uid/:token', element: <NewPassword /> },
        { path: 'verify/:uid/:token', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        // /dashboard is the home — renders GeneralApp directly.
        { element: <GeneralApp />, index: true },
        // Backward-compat: any bookmark or external link pointing to the
        // old /dashboard/app keeps working but lands on the canonical /.
        { path: 'app', element: <Navigate to={PATH_DASHBOARD.root} replace /> },
        {
          path: 'admin',
          children: [
            { element: <Navigate to="/dashboard/admin/metrics" replace />, index: true },
            { path: 'metrics', element: <AdminMetrics /> },
            { path: 'mapa', element: <MapaProyecto /> },
            { path: 'sendinblue', element: <Sendinblue /> },
            { path: 'tarot', element: <TarotRequests /> },
            { path: 'suenos', element: <SuenosRequests /> },
            { path: 'astralchart', element: <AstralChartRequests /> },
            { path: 'numerologia', element: <NumerologyRequests /> },
            { path: 'runas', element: <RunasRequests /> },
            { path: 'carta-nahual', element: <NahualChartRequests /> },
            { path: 'jobs', element: <JobRequests /> },
            { path: 'imageapproval', element: <ImageApproval /> },
            { path: 'courtesies', element: <Courtesies /> },
            { path: 'gift-codes', element: <GiftCodes /> },
            { path: 'chats-en-vivo', element: <ChatsEnVivo /> },

          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/account" replace />, index: true },
            { path: 'cards', element: <UserCards /> },
            { path: 'account', element: <UserAccount /> },
            { path: 'payments', element: <UserPayments /> },
          ],
        },
        {
          path: 'biopairs',
          children: [
            { element: <Biopairs />, index: true },
          ],
        },
        {
          path: 'tools',
          children: [
            { element: <Navigate to="/dashboard/tools/bach-flowers" replace />, index: true },
            {
              path: 'bach-flowers',
              children: [
                { element: <Navigate to="/dashboard/tools/bach-flowers/consulta" replace />, index: true },
                { path: 'consulta', element: <BachFlowersIndex /> },
                { path: 'completo', element: <BachFlowersFullIndex /> },
                { path: 'catalogo', element: <Navigate to="/dashboard/biblioteca/flores-de-bach" replace /> },
              ],
            },
            {
              path: 'chakras',
              children: [
                { element: <Navigate to="/dashboard/tools/chakras/consulta" replace />, index: true },
                { path: 'consulta', element: <ChakrasConsulta /> },
                { path: 'catalogo', element: <Navigate to="/dashboard/biblioteca/chakras" replace /> },
              ],
            },
            {
              path: 'numerology',
              children: [
                { element: <Navigate to="/dashboard/tools/numerology/consulta" replace />, index: true },
                { path: 'consulta', element: <NumerologyConsultaDashboard /> },
                // Productos de la familia que faltaban en el dashboard (a medida
                // $499, boda $349). Reusan las landings públicas; Numerología no
                // tiene suscripción, así que no hay perk de sub que regresar.
                { path: 'consulta-medida', element: <NumerologyConsultaMedida /> },
                { path: 'boda', element: <NumerologyBodaOverview /> },
                { path: 'lectura', element: <NumerologyLectura /> },
                { path: 'catalogo', element: <Navigate to="/dashboard/biblioteca/numerologia" replace /> },
              ],
            },
            {
              path: 'astralchart',
              children: [
                { element: <Navigate to="/dashboard/tools/astralchart/consulta" replace />, index: true },
                { path: 'consulta', element: <AstralChartConsultaDashboard /> },
                { path: 'catalogo', element: <Navigate to="/dashboard/biblioteca/planetas" replace /> },
              ],
            },
            {
              path: 'crystals',
              children: [
                { element: <Navigate to="/dashboard/tools/crystals/consulta" replace />, index: true },
                { path: 'consulta', element: <CrystalsConsulta /> },
                { path: 'catalogo', element: <Navigate to="/dashboard/biblioteca/cristales" replace /> },
              ],
            },
            // Sinastría: el dashboard regalaba la compatibilidad gratis
            // (SynastryJourney). Ahora reusa el flujo público de pago para no
            // canibalizar el producto ($599).
            {
              path: 'sinastria',
              children: [
                { element: <Navigate to="/dashboard/tools/sinastria/consulta" replace />, index: true },
                { path: 'consulta', element: <SinastriaConsultaPublic /> },
              ],
            },
            // Sueños: consulta = interpretación simple gratis (journey público);
            // profundo = lectura de pago ($349). Antes el dashboard regalaba la
            // lectura profunda "por plan".
            {
              path: 'suenos',
              children: [
                { element: <Navigate to="/dashboard/tools/suenos/consulta" replace />, index: true },
                { path: 'consulta', element: <SuenosConsultaPublic /> },
                // Lectura profunda gateada por perk SU en el dashboard: con perk
                // la genera gratis; sin perk muestra paywall que lleva a comprar.
                { path: 'profundo', element: <SuenosDashboardConsulta /> },
              ],
            },
            // Runas: consulta = una runa gratis (journey público); tirada = las
            // tres Nornas de pago ($599). Antes el dashboard regalaba la tirada
            // de 3 en pantalla.
            {
              path: 'runas',
              children: [
                { element: <Navigate to="/dashboard/tools/runas/consulta" replace />, index: true },
                { path: 'consulta', element: <RunasConsultaPublic /> },
                // Tirada de las Nornas gateada por perk RN en el dashboard: con
                // perk la genera gratis; sin perk muestra paywall a comprar.
                { path: 'tirada', element: <RunasDashboardConsulta /> },
              ],
            },
            // Nahual + Carta Nahual: reusan los componentes públicos (descubrir
            // gratis + checkout de Carta Nahual) montados dentro del dashboard
            // para que el usuario cautivo pueda descubrir y comprar sin salir.
            {
              path: 'nahual',
              children: [
                { element: <Navigate to="/dashboard/tools/nahual/descubrir" replace />, index: true },
                { path: 'descubrir', element: <NahualDescubrir /> },
                { path: 'carta', element: <CartaNahual /> },
              ],
            },
            // Quiromancia: abre directo en la lectura completa (de pago) — el
            // cautivo tiene mayor intención; el ejemplo queda como sub-ruta.
            {
              path: 'quiromancia',
              children: [
                { element: <Navigate to="/dashboard/tools/quiromancia/completa" replace />, index: true },
                { path: 'completa', element: <QuiromanciaCompleta /> },
                { path: 'lectura', element: <QuiromanciaLectura /> },
              ],
            },
          ],
        },
        {
          path: 'biblioteca',
          children: [
            { element: <Navigate to="/dashboard/biblioteca/flores-de-bach" replace />, index: true },
            { path: 'flores-de-bach', element: <DashboardBachFlowersCatalog /> },
            { path: 'chakras', element: <DashboardChakrasCatalog /> },
            { path: 'numerologia', element: <NumerologyCatalogDashboard /> },
            { path: 'planetas', element: <AstralChartCatalogDashboard /> },
            { path: 'cristales', element: <CrystalsCatalogo /> },
            { path: 'mis-recursos', element: <MisRecursosDashboard /> },
          ],
        },
        {
          path: 'mystic',
          children: [
            { element: <Navigate to="/dashboard/mystic" replace />, index: true },
            {
              path: 'tarot',
              children: [
                { element: <TarotHub />, index: true },
                { path: 'single', element: <TarotIndex /> },
                { path: 'daily', element: <TarotDailyPersonalized /> },
                { path: 'ask', element: <TarotThreeCards /> },
                { path: 'history', element: <TarotReading /> },
                // Legacy: /reading → history (mantener para no romper links)
                { path: 'reading', element: <Navigate to="/dashboard/mystic/tarot/history" replace /> },
              ],
            },
          ],
        },
        {
          path: 'store',
          children: [
            { element: <Navigate to="/dashboard/store/my-orders" replace />, index: true },
            { path: 'memberships', element: <Memberships /> },
            { path: 'memberships/:productId', element: <Membership /> },
            { path: 'payment/memberships', element: <Payment /> },
            { path: 'payment/paypal', element: <PaypalReturn /> },
            { path: 'my-orders', element: <MyOrders /> },
          ],
        },
        {
          path: 'support',
          children: [
            { element: <Navigate to="/dashboard/support/chat" replace />, index: true },
            { path: 'chat', element: <SupportChat /> },
            { path: 'guide', element: <SupportGuide /> }
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
      ],
    },

    // En prod nginx manda /optout/ a Django (landing crm); Django redirige
    // aquí cuando el token resulta ser del sistema de outreach a terapeutas.
    // La ruta vieja /optout/:token se mantiene para dev (sin nginx).
    {
      path: 'outreach/optout/:token',
      element: <OutreachOptout />,
    },
    {
      path: 'optout/:token',
      element: <OutreachOptout />,
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        // Consolidado en /tienda (decisión 2026-06-11); redirect para links viejos
        { path: 'pricing', element: <Navigate to="/tienda" replace /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <MainLayout navConfig={menuConfig} />,
      children: [
        { element: <HomePage />, index: true },
        {
          path: 'regalo/lectura-tarot',
          element: (
            <GuestGuard>
              <RegaloLecturaTarot />
            </GuestGuard>
          ),
        },
        // URLs cortas para ads (ocultas, no van en el menú). Renderizan la
        // MISMA landing canónica que se ve desde el menú: free = el journey
        // (igual que /tools/nahual/descubrir), pagada = Carta Nahual (igual que
        // /tools/carta-nahual). Sin GuestGuard: flujos anónimos.
        { path: 'nahual-gratis', element: <NahualDescubrir /> },
        { path: 'carta-nahual', element: <CartaNahual /> },
        // LAB — juegos de captación para ads (ocultos, no van en el menú).
        { path: 'lab/numero-intuicion', element: <LabNumeroIntuicion /> },
        // Horóscopo diario público (SEO: tráfico orgánico recurrente del nicho).
        { path: 'horoscopo', element: <HoroscopoDiario /> },
        { path: 'horoscopo/:signo', element: <HoroscopoDiario /> },
        { path: 'about-us', element: <About /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <Faqs /> },
        { path: 'privacy', element: <Privacy /> },
        { path: 'terms', element: <Terms /> },
        { path: 'delete-account', element: <DeleteAccount /> },
        // Carta Numerológica · carta de ejemplo pública (showcase del producto).
        { path: 'numerologia/carta-numerologica/ejemplo', element: <NumerologyEjemplo /> },
        {
          path: 'tools',
          children: [
            { element: <Navigate to="/tools/tarot" replace />, index: true },
            { path: 'directory', element: <DirectoryOverview /> },
            { path: 'tarot', element: <TarotOverview /> },
            { path: 'tarot/gratis', element: <Navigate to="/tools/tarot/consulta" replace /> },
            { path: 'tarot/consulta', element: <TarotConsultaPublic /> },
            { path: 'tarot/r/:token', element: <TarotFreeReadingPublic /> },
            { path: 'tarot/gracias', element: <TarotThankYou /> },
            { path: 'tarot/three-cards', element: <TarotThreeCardsPublic /> },
            { path: 'tarot/lectura', element: <TarotLectura /> },
            { path: 'bach-flowers', element: <BachFlowersOverview /> },
            { path: 'bach-flowers/gratis', element: <BachFlowersFreeLanding /> },
            { path: 'bach-flowers/consulta', element: <BachFlowersIndex /> },
            { path: 'bach-flowers/result', element: <BachFlowersIndex /> },
            { path: 'bach-flowers/catalogo', element: <Navigate to="/biblioteca/flores-de-bach" replace /> },
            { path: 'chakras', element: <ChakrasOverview /> },
            { path: 'chakras/consulta', element: <ChakrasConsultaPublic /> },
            { path: 'chakras/catalogo', element: <Navigate to="/biblioteca/chakras" replace /> },
            { path: 'biopairs', element: <BiopairsOverview /> },
            { path: 'numerology', element: <NumerologyOverview /> },
            { path: 'numerology/carta', element: <NumerologyCartaOverview /> },
            { path: 'numerology/boda/inicio', element: <NumerologyBodaOverview /> },
            { path: 'numerology/boda', element: <NumerologyBodaFree /> },
            { path: 'numerology/boda/libro', element: <NumerologyBoda /> },
            { path: 'numerology/boda/r/:token', element: <NumerologyBodaReadingPublic /> },
            { path: 'numerology/boda/lectura/r/:token', element: <NumerologyBodaLectura /> },
            { path: 'numerology/boda/cielo/r/:token', element: <NumerologyBodaCielo /> },
            { path: 'numerology/gratis', element: <Navigate to="/tools/numerology/consulta" replace /> },
            { path: 'numerology/completa', element: <NumerologyCompleta /> },
            { path: 'numerology/consulta', element: <NumerologyConsultaPublic /> },
            { path: 'numerology/consulta-medida', element: <NumerologyConsultaMedida /> },
            { path: 'numerology/r/:token', element: <NumerologyFreeReadingPublic /> },
            { path: 'numerology/gracias', element: <NumerologyThankYou /> },
            { path: 'numerology/lectura', element: <NumerologyLectura /> },
            { path: 'numerology/catalogo', element: <Navigate to="/biblioteca/numerologia" replace /> },
            { path: 'astralchart', element: <AstralChartOverview /> },
            { path: 'astralchart/gratis', element: <Navigate to="/tools/astralchart/consulta" replace /> },
            { path: 'astralchart/completa', element: <AstralChartCompleta /> },
            { path: 'astralchart/consulta', element: <AstralChartConsultaPublic /> },
            { path: 'astralchart/r/:token', element: <AstralFreeReadingPublic /> },
            { path: 'astralchart/gracias', element: <AstralThankYou /> },
            { path: 'astralchart/descargar', element: <AstralChartDownload /> },
            { path: 'astralchart/lectura', element: <AstralChartLectura /> },
            { path: 'carta-nahual', element: <CartaNahual /> },
            { path: 'carta-nahual/lectura', element: <NahualLectura /> },
            { path: 'carta-nahual/gracias', element: <NahualThankYou /> },
            { path: 'astralchart/catalogo', element: <Navigate to="/biblioteca/planetas" replace /> },
            { path: 'crystals', element: <CrystalsOverview /> },
            { path: 'crystals/consulta', element: <CrystalsConsulta /> },
            { path: 'crystals/r/:token', element: <CrystalsFreeReadingPublic /> },
            { path: 'crystals/catalogo', element: <Navigate to="/biblioteca/cristales" replace /> },
            { path: 'sinastria', element: <Navigate to="/tools/sinastria/consulta" replace /> },
            { path: 'sinastria/consulta', element: <SinastriaConsultaPublic /> },
            { path: 'sinastria/lectura', element: <SinastriaLectura /> },
            { path: 'suenos', element: <SuenosOverview /> },
            { path: 'suenos/gratis', element: <Navigate to="/tools/suenos/consulta" replace /> },
            { path: 'suenos/consulta', element: <SuenosConsultaPublic /> },
            { path: 'suenos/r/:token', element: <SuenosFreeReadingPublic /> },
            { path: 'suenos/gracias', element: <SuenosThankYou /> },
            { path: 'suenos/profundo', element: <SuenosProfundoPublic /> },
            { path: 'suenos/lectura', element: <SuenosLectura /> },
            { path: 'runas', element: <RunasOverview /> },
            { path: 'runas/gratis', element: <Navigate to="/tools/runas/consulta" replace /> },
            { path: 'runas/consulta', element: <RunasConsultaPublic /> },
            { path: 'runas/r/:token', element: <RunasFreeReadingPublic /> },
            { path: 'runas/gracias', element: <RunasThankYou /> },
            { path: 'runas/tirada', element: <RunasTiradaPublic /> },
            { path: 'runas/lectura', element: <RunasLectura /> },
            { path: 'nahual', element: <NahualOverview /> },
            { path: 'nahual/preview', element: <NahualPreview /> },
            { path: 'nahual/descubrir', element: <NahualDescubrir /> },
            { path: 'nahual/r/:token', element: <NahualShared /> },
            { path: 'nahual/mas/:token', element: <NahualRecommendationsPublic /> },
            { path: 'quiromancia', element: <QuiromanciaOverview /> },
            { path: 'quiromancia/gratis', element: <QuiromanciaGratis /> },
            { path: 'quiromancia/completa', element: <QuiromanciaCompleta /> },
            { path: 'quiromancia/gracias', element: <QuiromanciaThankYou /> },
            { path: 'quiromancia/lectura', element: <QuiromanciaLectura /> },
            // LAB — Vidas pasadas movida a /tools/lab (oculta del menú, para ads).
            { path: 'lab/vidas-pasadas', element: <PastLivesOverview /> },
            { path: 'lab/vidas-pasadas/r/:token', element: <PastLivesShared /> },
            // Compat: URLs viejas siguen vivas (links compartidos); el overview redirige.
            { path: 'vidas-pasadas', element: <Navigate to="/tools/lab/vidas-pasadas" replace /> },
            { path: 'vidas-pasadas/r/:token', element: <PastLivesShared /> },
          ]
        },
        {
          path: 'biblioteca',
          children: [
            { path: 'flores-de-bach', element: <BachFlowersCatalog /> },
            { path: 'chakras', element: <ChakrasCatalog /> },
            { path: 'numerologia', element: <NumerologyCatalogPublic /> },
            { path: 'planetas', element: <AstralChartCatalogPublic /> },
            { path: 'cristales', element: <CrystalsCatalogo /> },
            { path: 'nahual', element: <NahualCatalog /> },
            { path: 'nahual/:slug', element: <NahualSignPublic /> },
            { path: 'vidas-pasadas', element: <PastLivesCatalog /> },
            { path: 'vidas-pasadas/:slug', element: <PastLifeFigurePublic /> },
          ]
        },
        { path: 'tienda', element: <PublicStore /> },
        { path: 'tienda/pago', element: <StorePayment /> },
        // Páginas predefinidas por número (1-9, 11, 22, 33). Destino del
        // share bar de numerología — SEO-indexables, OG-compartibles.
        { path: 'numerologia/numero/:n', element: <NumerologyNumberPublic /> },
        {
          path: 'blog',
          children: [
            { element: <Blog />, index: true },
            { path: 'posts', element: <BlogPosts /> },
            { path: 'post/:slug', element: <BlogPost /> },
            { path: 'new', element: <BlogNewPost /> },
          ],
        },
        {
          path: 'directory',
          children: [
            { element: <Directory />, index: true, },
            { path: 'therapist/:id', element: <UserTherapist /> },
          ]
        }
      ],
    },

    // Prerelease Routes
    {
      path: 'prerelease',
      element: <MainLayout navConfig={menuPrerelease} />,
      children: [
        { element: <PrereleasePage />, index: true },
        { path: 'prerelease/refered_by/:referer', element: <PrereleasePage /> },
      ]
    },

{ path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));

// DASHBOARD ----------------------------------------------------------------------

// OVERVIEW TOOLS
const TarotOverview = Loadable(lazy(() => import('../pages/tools/Tarot')));
const BachFlowersOverview = Loadable(lazy(() => import('../pages/tools/BachFlowersOverview')));
const BachFlowersFreeLanding = Loadable(lazy(() => import('../pages/tools/BachFlowers')));
const BachFlowersCatalog = Loadable(lazy(() => import('../pages/tools/BachFlowersCatalog')));
const BiopairsOverview = Loadable(lazy(() => import('../pages/tools/Biopairs')));
const ChakrasOverview = Loadable(lazy(() => import('../pages/tools/Chakras')));
const ChakrasCatalog = Loadable(lazy(() => import('../pages/tools/ChakrasCatalog')));
const ChakrasConsultaPublic = Loadable(lazy(() => import('../pages/dashboard/tools/ChakrasConsulta')));
const NumerologyOverview = Loadable(lazy(() => import('../pages/tools/NumerologyOverview')));
const NumerologyCartaOverview = Loadable(lazy(() => import('../pages/tools/NumerologyCartaOverview')));
const NumerologyBodaFree = Loadable(lazy(() => import('../pages/tools/NumerologyBodaFree')));
const NumerologyBoda = Loadable(lazy(() => import('../pages/tools/NumerologyBoda')));
const NumerologyBodaOverview = Loadable(lazy(() => import('../pages/tools/NumerologyBodaOverview')));
const NumerologyBodaReadingPublic = Loadable(lazy(() => import('../pages/tools/NumerologyBodaReadingPublic')));
const NumerologyBodaLectura = Loadable(lazy(() => import('../pages/tools/NumerologyBodaLectura')));
const NumerologyBodaCielo = Loadable(lazy(() => import('../pages/tools/NumerologyBodaCielo')));
const NumerologyCompleta = Loadable(lazy(() => import('../pages/tools/NumerologyCompleta')));
const NumerologyConsultaPublic = Loadable(lazy(() => import('../pages/tools/NumerologyConsulta')));
const NumerologyConsultaMedida = Loadable(lazy(() => import('../pages/tools/NumerologyConsultaMedida')));
const NumerologyFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/NumerologyFreeReadingPublic')));
const NumerologyThankYou = Loadable(lazy(() => import('../pages/tools/NumerologyThankYou')));
const NumerologyLectura = Loadable(lazy(() => import('../pages/tools/NumerologyLectura')));
const NumerologyEjemplo = Loadable(lazy(() => import('../pages/tools/NumerologyEjemplo')));
const NumerologyCatalogPublic = Loadable(lazy(() => import('../pages/tools/NumerologyCatalog')));
const NumerologyNumberPublic = Loadable(lazy(() => import('../pages/tools/NumerologyNumberPublic')));
const AstralChartOverview = Loadable(lazy(() => import('../pages/tools/AstralChartOverview')));
const AstralChartCompleta = Loadable(lazy(() => import('../pages/tools/AstralChartCompleta')));
const AstralChartConsultaPublic = Loadable(lazy(() => import('../pages/tools/AstralChartConsulta')));
const AstralFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/AstralFreeReadingPublic')));
const AstralThankYou = Loadable(lazy(() => import('../pages/tools/AstralThankYou')));
const AstralChartCatalogPublic = Loadable(lazy(() => import('../pages/tools/AstralChartCatalog')));
const AstralChartDownload = Loadable(lazy(() => import('../pages/tools/AstralChartDownload')));
const AstralChartLectura = Loadable(lazy(() => import('../pages/tools/AstralChartLectura')));
const NahualLectura = Loadable(lazy(() => import('../pages/tools/NahualLectura')));
const NahualThankYou = Loadable(lazy(() => import('../pages/tools/NahualThankYou')));
const CartaNahual = Loadable(lazy(() => import('../pages/tools/CartaNahual')));
const CrystalsOverview = Loadable(lazy(() => import('../pages/tools/Crystals')));
const CrystalsCatalogo = Loadable(lazy(() => import('../pages/tools/CrystalsCatalogo')));
const DirectoryOverview = Loadable(lazy(() => import('../pages/tools/Directory')));
const SuenosOverview = Loadable(lazy(() => import('../pages/tools/SuenosOverview')));
const SuenosConsultaPublic = Loadable(lazy(() => import('../pages/tools/SuenosConsulta')));
const SuenosFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/SuenosFreeReadingPublic')));
const SuenosThankYou = Loadable(lazy(() => import('../pages/tools/SuenosThankYou')));
const SuenosProfundoPublic = Loadable(lazy(() => import('../pages/tools/SuenosProfundo')));
const SuenosDashboardConsulta = Loadable(lazy(() => import('../pages/dashboard/tools/SuenosConsulta')));
const RunasDashboardConsulta = Loadable(lazy(() => import('../pages/dashboard/tools/RunasConsulta')));
const SuenosLectura = Loadable(lazy(() => import('../pages/tools/SuenosLectura')));
const RunasLectura = Loadable(lazy(() => import('../pages/tools/RunasLectura')));
const RunasOverview = Loadable(lazy(() => import('../pages/tools/RunasOverview')));
const RunasConsultaPublic = Loadable(lazy(() => import('../pages/tools/RunasConsulta')));
const RunasFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/RunasFreeReadingPublic')));
const RunasThankYou = Loadable(lazy(() => import('../pages/tools/RunasThankYou')));
const RunasTiradaPublic = Loadable(lazy(() => import('../pages/tools/RunasTirada')));
const NahualOverview = Loadable(lazy(() => import('../pages/tools/NahualOverview')));
const NahualPreview = Loadable(lazy(() => import('../pages/tools/NahualPreview')));
const NahualDescubrir = Loadable(lazy(() => import('../pages/tools/Nahual')));
const QuiromanciaOverview = Loadable(lazy(() => import('../pages/tools/QuiromanciaOverview')));
const QuiromanciaGratis = Loadable(lazy(() => import('../pages/tools/QuiromanciaGratis')));
const QuiromanciaCompleta = Loadable(lazy(() => import('../pages/tools/QuiromanciaCompleta')));
const QuiromanciaThankYou = Loadable(lazy(() => import('../pages/tools/QuiromanciaThankYou')));
const QuiromanciaLectura = Loadable(lazy(() => import('../pages/tools/QuiromanciaLectura')));
const NahualShared = Loadable(lazy(() => import('../pages/tools/NahualShared')));
const NahualRecommendationsPublic = Loadable(lazy(() => import('../pages/tools/NahualRecommendationsPublic')));
const NahualCatalog = Loadable(lazy(() => import('../pages/tools/NahualCatalog')));
const NahualSignPublic = Loadable(lazy(() => import('../pages/tools/NahualSignPublic')));
const PastLivesOverview = Loadable(lazy(() => import('../pages/tools/PastLives')));
const PastLivesShared = Loadable(lazy(() => import('../pages/tools/PastLivesShared')));
const PastLivesCatalog = Loadable(lazy(() => import('../pages/tools/PastLivesCatalog')));
const PastLifeFigurePublic = Loadable(lazy(() => import('../pages/tools/PastLifeFigurePublic')));

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));

// USER
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserPayments = Loadable(lazy(() => import('../pages/dashboard/user/UserPayments')));

// ADMIN
const AdminMetrics = Loadable(lazy(() => import('../pages/dashboard/admin/AdminMetrics')));
const MapaProyecto = Loadable(lazy(() => import('../pages/dashboard/admin/MapaProyecto')));
const Sendinblue = Loadable(lazy(() => import('../pages/dashboard/admin/Sendinblue')));
const TarotRequests = Loadable(lazy(() => import('../pages/dashboard/admin/TarotRequests')));
const SuenosRequests = Loadable(lazy(() => import('../pages/dashboard/admin/SuenosRequests')));
const AstralChartRequests = Loadable(lazy(() => import('../pages/dashboard/admin/AstralChartRequests')));
const NumerologyRequests = Loadable(lazy(() => import('../pages/dashboard/admin/NumerologyRequests')));
const RunasRequests = Loadable(lazy(() => import('../pages/dashboard/admin/RunasRequests')));
const NahualChartRequests = Loadable(
  lazy(() => import('../pages/dashboard/admin/NahualChartRequests'))
);
const JobRequests = Loadable(lazy(() => import('../pages/dashboard/admin/JobRequests')));
const ImageApproval = Loadable(lazy(() => import('../pages/dashboard/admin/ImageApproval')));
const Courtesies = Loadable(lazy(() => import('../pages/dashboard/admin/Courtesies')));
const GiftCodes = Loadable(lazy(() => import('../pages/dashboard/admin/GiftCodes')));
const ChatsEnVivo = Loadable(lazy(() => import('../pages/dashboard/admin/ChatsEnVivo')));

// BIOPAIRS
const Biopairs = Loadable(lazy(() => import('../pages/dashboard/Biopairs')));

// MYSTIC
const TarotIndex = Loadable(lazy(() => import('../pages/dashboard/mystic/TarotIndex')));
const TarotReading = Loadable(lazy(() => import('../pages/dashboard/mystic/TarotReading')));
const TarotThreeCards = Loadable(lazy(() => import('../pages/dashboard/mystic/TarotThreeCards')));
const TarotHub = Loadable(lazy(() => import('../pages/dashboard/mystic/TarotHub')));
const TarotDailyPersonalized = Loadable(lazy(() => import('../pages/dashboard/mystic/TarotDailyPersonalized')));
const BachFlowersIndex = Loadable(lazy(() => import('../pages/dashboard/mystic/BachFlowersIndex')));
const BachFlowersFullIndex = Loadable(lazy(() => import('../pages/dashboard/tools/BachFlowersFullIndex')));
const DashboardBachFlowersCatalog = Loadable(lazy(() => import('../pages/dashboard/tools/BachFlowersCatalogDashboard')));
const ChakrasConsulta = Loadable(lazy(() => import('../pages/dashboard/tools/ChakrasConsulta')));
const DashboardChakrasCatalog = Loadable(lazy(() => import('../pages/dashboard/tools/ChakrasCatalogDashboard')));
const NumerologyConsultaDashboard = Loadable(lazy(() => import('../pages/dashboard/tools/NumerologyConsulta')));
const NumerologyCatalogDashboard = Loadable(lazy(() => import('../pages/dashboard/tools/NumerologyCatalogDashboard')));
const AstralChartConsultaDashboard = Loadable(lazy(() => import('../pages/dashboard/tools/AstralChartConsulta')));
const AstralChartCatalogDashboard = Loadable(lazy(() => import('../pages/dashboard/tools/AstralChartCatalogDashboard')));
const MisRecursosDashboard = Loadable(lazy(() => import('../pages/dashboard/tools/MisRecursosDashboard')));
const CrystalsConsulta = Loadable(lazy(() => import('../pages/dashboard/tools/CrystalsConsulta')));
const CrystalsFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/CrystalsFreeReadingPublic')));
const SinastriaConsultaPublic = Loadable(lazy(() => import('../pages/tools/SinastriaConsulta')));
const SinastriaLectura = Loadable(lazy(() => import('../pages/tools/SinastriaLectura')));

// SHOP
const Payment = Loadable(lazy(() => import('../pages/dashboard/shop/Payment')));
const Membership = Loadable(lazy(() => import('../pages/dashboard/shop/Membership')));
const Memberships = Loadable(lazy(() => import('../pages/dashboard/shop/Memberships')));
const MyOrders = Loadable(lazy(() => import('../pages/dashboard/shop/MyOrders')));
const PaypalReturn = Loadable(lazy(() => import('../pages/dashboard/shop/PaypalReturn')));

// SUPPORT
const SupportChat = Loadable(lazy(() => import('../pages/dashboard/support/Chat')));
const SupportGuide = Loadable(lazy(() => import('../pages/dashboard/support/Guide')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// PAGE ----------------------------------------------------------------------

// BLOG
const Blog = Loadable(lazy(() => import('../pages/Blog')));
const BlogPost = Loadable(lazy(() => import('../pages/BlogPost')));
const BlogPosts = Loadable(lazy(() => import('../pages/BlogPosts')));
const BlogNewPost = Loadable(lazy(() => import('../pages/BlogNewPost')));

// DIRECTORY
const Directory = Loadable(lazy(() => import('../pages/Directory')));
const UserTherapist = Loadable(lazy(() => import('../pages/dashboard/UserTherapist')));

// MISTICO
const TarotThreeCardsPublic = Loadable(lazy(() => import('../pages/mystic/TarotThreeCardsPublic')));
const TarotConsultaPublic = Loadable(lazy(() => import('../pages/tools/TarotConsulta')));
const TarotFreeReadingPublic = Loadable(lazy(() => import('../pages/tools/TarotFreeReadingPublic')));
const TarotThankYou = Loadable(lazy(() => import('../pages/tools/TarotThankYou')));
const TarotLectura = Loadable(lazy(() => import('../pages/mystic/TarotLectura')));

// CRYSTAL TEST PAGES

// PRERELEASE
const PrereleasePage = Loadable(lazy(() => import('../pages/Prerelease')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Privacy = Loadable(lazy(() => import('../pages/Privacy')));
const Terms = Loadable(lazy(() => import('../pages/Terms')));
const DeleteAccount = Loadable(lazy(() => import('../pages/DeleteAccount')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

// PUBLIC STORE
const PublicStore = Loadable(lazy(() => import('../pages/store/Store')));
const StorePayment = Loadable(lazy(() => import('../pages/store/StorePayment')));

// Campaings
const RegaloLecturaTarot = Loadable(lazy(() => import('../pages/campaings/RegaloLecturaTarot')));

// Lab (juegos de captación para ads)
const LabNumeroIntuicion = Loadable(lazy(() => import('../pages/lab/LabNumeroIntuicion')));
const HoroscopoDiario = Loadable(lazy(() => import('../pages/horoscopo/HoroscopoDiario')));
const OutreachOptout = Loadable(lazy(() => import('../pages/OutreachOptout')));